import {skipToken} from '@reduxjs/toolkit/query';
import {Alert} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import {useLocale} from 'platform/locale';
import styled from 'styled-components';

import {isNil, join, pipe, reject} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetMakeModelWithMakeQuery,
  useGetVehicleMileageQuery,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {FullVehicleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {urlBuilder} from '@dms/routes';
import {createSearchParamsString, useGetVehicleLabels, Widget} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getEffectiveMileage} from './utils/getEffectiveMileage';

const REQUIRED_VEHICLE_FIELDS: {
  i18nKey: string;
  getValue: (v: FullVehicleResponseBody) => unknown;
}[] = [
  {i18nKey: 'entity.vehicle.labels.body', getValue: (v) => v.bodyStyle},
  {i18nKey: 'entity.vehicle.labels.engineCapacity', getValue: (v) => v.engine?.engineVolume},
  {i18nKey: 'entity.vehicle.labels.transmission', getValue: (v) => v.transmission},
  {i18nKey: 'entity.vehicle.labels.fuel', getValue: (v) => v.fuelType},
  {i18nKey: 'entity.vehicle.labels.modelYear', getValue: (v) => v.manufacturedOnYear},
];

function getMissingVehicleFieldLabels(
  vehicle: FullVehicleResponseBody,
  effectiveMileage: number | Nullish
): string[] {
  const missingStaticFields = REQUIRED_VEHICLE_FIELDS.filter((field) =>
    isNil(field.getValue(vehicle))
  ).map((field) => i18n.t(field.i18nKey));

  return isNil(effectiveMileage)
    ? [...missingStaticFields, i18n.t('entity.vehicle.labels.mileage')]
    : missingStaticFields;
}

interface MiraPredictionWidgetProps extends TestIdProps {
  vehicleId: string;
}

export function MiraPredictionWidget(props: MiraPredictionWidgetProps) {
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {data: latestMileageRecord} = useGetVehicleMileageQuery({vehicleId: props.vehicleId});
  const {currentData: logoMakes, isFetching: isLogoLoading} = useGetMakeModelWithMakeQuery(
    isNil(vehicle?.make) || isNil(vehicle?.type)
      ? skipToken
      : {vehicleType: vehicle.type, make: vehicle.make, logoUrl: true}
  );
  const {language} = useLocale();
  const [vehicleLabels] = useGetVehicleLabels({
    make: vehicle?.make,
    vehicleType: vehicle?.type,
    modelFamily: vehicle?.modelFamily,
    trim: vehicle?.trim,
    variant: vehicle?.variant,
  });

  if (isNil(vehicle?.make) || isNil(vehicle?.modelFamily)) {
    return null;
  }

  const isMakeLabelMissing = isNilOrEmpty(vehicleLabels.make);
  const modelTitle = isMakeLabelMissing
    ? undefined
    : pipe(
        reject(isNilOrEmpty),
        join(' ')
      )([vehicleLabels.modelFamily, vehicleLabels.trim, vehicleLabels.variant]);

  const effectiveMileage = getEffectiveMileage(latestMileageRecord, vehicle.vehicle);
  const missingFieldLabels = getMissingVehicleFieldLabels(vehicle, effectiveMileage);
  const hasMissingFields = missingFieldLabels.length > 0;

  const miraDomain = process.env.MIRA_DOMAIN;
  const isMiraDomainUnconfigured = isNil(miraDomain);

  const handleOpenMira = () => {
    if (isNil(miraDomain)) {
      return;
    }

    const miraUrl = urlBuilder({
      protocol: window.location.protocol,
      workspace: undefined,
      domain: miraDomain,
      route: '/report',
      searchParam: createSearchParamsString({
        vin: vehicle?.vin ?? undefined,
        make: vehicle?.make ?? undefined,
        modelFamily: vehicle?.modelFamily ?? undefined,
        makeLabel: vehicleLabels.make,
        catalogueLogoUrl:
          logoMakes?.find((item) => item.make === vehicle.make)?.logo_url_2x?.trim() || undefined,
        modelTitle: isNilOrEmpty(modelTitle) ? undefined : modelTitle,
        bodyStyle: vehicle?.bodyStyle ?? undefined,
        engineVolume: vehicle?.engine?.engineVolume?.toString() ?? undefined,
        transmission: vehicle?.transmission ?? undefined,
        fuelType: vehicle?.fuelType ?? undefined,
        modelYear: vehicle?.manufacturedOnYear?.toString() ?? undefined,
        firstRegistration: vehicle?.vehicle.firstRegistrationOn ?? undefined,
        mileage: effectiveMileage?.toString() ?? undefined,
        lng: language,
      }),
    });
    window.open(miraUrl, '_blank')?.focus();
  };

  return (
    <VStack spacing={2}>
      <Widget.Card data-testid={suffixTestId('miraPredictionWidget', props)}>
        <Widget.Header
          logo={<MiraLogo />}
          title={i18n.t('page.integrations.labels.miraPredictionTitle')}
          subtitle={
            <Text size="xSmall" color="secondary">
              {i18n.t('page.integrations.labels.miraPredictionDescription')}
            </Text>
          }
          actions={[
            {
              type: 'button',
              variant: 'secondary',
              rightIcon: 'action/launch',
              isDisabled: hasMissingFields || isMiraDomainUnconfigured || isLogoLoading,
              onClick: handleOpenMira,
              title: i18n.t('page.integrations.labels.miraPredictionOpenReport'),
              'data-testid': suffixTestId('miraPredictionWidget-openReport', props),
            },
          ]}
          data-testid={suffixTestId('miraPredictionWidget', props)}
        />
      </Widget.Card>
      <Show when={hasMissingFields}>
        <Alert
          type="inline"
          variant="warning"
          message={`${i18n.t('page.integrations.labels.miraPredictionMissingFields')}: ${missingFieldLabels.join(', ')}`}
        />
      </Show>
    </VStack>
  );
}

const MIRA_LOGO_LABEL = 'AI PRES';

function MiraLogo() {
  return (
    <Box backgroundColor="palettes.neutral.900.100" width="100%" height="100%">
      <MiraLogoContainer>
        <VStack justify="center" align="center" height="100%">
          <MiraLogoText>{MIRA_LOGO_LABEL}</MiraLogoText>
        </VStack>
      </MiraLogoContainer>
    </Box>
  );
}

// Container query enables font-size to scale with the parent box rendered by
// Widget.Header (96px on desktop, 64px on mobile) without coupling to specific
// pixel values. 15cqh keeps the original 14px feel on desktop (14/96 ≈ 15%).
const MiraLogoContainer = styled.div`
  width: 100%;
  height: 100%;
  container-type: size;
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const MiraLogoText = styled.span`
  color: white;
  font-size: 15cqh;
  font-weight: 700;
  letter-spacing: 0.2em;
`;
